import React, { useEffect, useMemo, useState } from 'react';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../../context/organization';
import { trackInviteAccept, trackInviteDecline, trackInviteViewed } from '../../analytics';

type InviteResponse = {
  invite: {
    code: string;
    email: string;
    org_name: string;
    role?: 'admin' | 'member' | string;
    invited_by_email?: string;
    expires_at?: string; // ISO
  };
};

const parseAxiosErr = (err: unknown) => {
  const e = err as AxiosError<any>;
  return e?.response?.data?.message || e?.message || 'Request failed';
};

const AcceptInvitePage: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { user, getAccessTokenSilently } = useAuth0();
  const { organization, loginUser } = useOrganization();

  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState<'accept' | 'decline' | null>(null);
  const [inviteData, setInviteData] = useState<InviteResponse['invite'] | null>(null);
  const [mismatch, setMismatch] = useState(false);

  const myEmail = user?.email || '';

  // show a friendly expires label if available
  const expiresLabel = useMemo(() => {
    if (!inviteData?.expires_at) return null;
    try {
      const d = new Date(inviteData.expires_at);
      return isNaN(d.getTime()) ? null : d.toLocaleString();
    } catch {
      return null;
    }
  }, [inviteData?.expires_at]);

  // ESC -> back home
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate('/');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  useEffect(() => {
    // If already in an org, bounce to home (no need to accept)
    if (organization) {
      navigate('/');
      return;
    }

    if (!inviteCode) {
      toast.error('Missing invite code');
      navigate('/');
      return;
    }

    (async () => {
      try {
        if (!myEmail) return; // wait for user to load
        const token = await getAccessTokenSilently();
        const res = await axios.get<InviteResponse>(
          `${process.env.REACT_APP_API_BASE_URL}/organization/invite/${inviteCode}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const invite = res.data.invite;
        setInviteData(invite);

        // If invite is not for the logged-in user, show a mismatch UI
        if (invite.email.toLowerCase() !== myEmail.toLowerCase()) {
          setMismatch(true);
        }
      } catch (err) {
        if (axios.isCancel(err)) return;
        toast.error(parseAxiosErr(err));
        navigate('/');
      } finally {
        setLoading(false);
      }
    })();
    // include everything we read
  }, [inviteCode, myEmail, getAccessTokenSilently, navigate, organization]);

  useEffect(() => {
    if (!inviteData || !myEmail) return;
    const hasMismatch = inviteData.email.toLowerCase() !== myEmail.toLowerCase();
    trackInviteViewed({ has_mismatch: hasMismatch, role: inviteData.role || 'member' });
  }, [inviteData, myEmail]);

  const handleAccept = async () => {
    if (!user || !inviteCode) return;
    try {
      setSubmitLoading('accept');
      const token = await getAccessTokenSilently();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/organization/invite/accept`,
        { code: inviteCode, user_info: user },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loginUser(user);
      trackInviteAccept({ has_mismatch: mismatch });
      toast.success('You have joined the organization!');
      navigate('/');
    } catch (err) {
      toast.error(parseAxiosErr(err));
    } finally {
      setSubmitLoading(null);
    }
  };

  const handleDecline = async () => {
    if (!inviteCode) return navigate('/');
    try {
      setSubmitLoading('decline');
      const token = await getAccessTokenSilently();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/organization/invite/decline`,
        { code: inviteCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      trackInviteDecline();
      toast.info('Invitation declined.');
      navigate('/');
    } catch (err) {
      toast.error(parseAxiosErr(err));
    } finally {
      setSubmitLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-100 to-blue-100 px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-2/3 rounded bg-gray-200"></div>
            <div className="h-4 w-full rounded bg-gray-200"></div>
            <div className="h-4 w-5/6 rounded bg-gray-200"></div>
            <div className="mt-6 h-10 w-full rounded bg-blue-200"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!inviteData) {
    return null;
  }

  const wrongEmail = mismatch ? (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
      This invite is for <b>{inviteData.email}</b>, but you’re logged in as <b>{myEmail || 'unknown'}</b>.
      Please switch accounts and try again.
    </div>
  ) : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-100 to-blue-100 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg">
        <div className="text-center">
          <h1 className="mb-2 text-3xl font-bold text-gray-800">🎉 You’re Invited!</h1>
          <p className="mb-6 text-gray-600">
            You’ve been invited to join <strong>{inviteData.org_name}</strong>.
          </p>

          <div className="mx-auto mb-6 w-full rounded-xl border border-gray-200 bg-gray-50 p-4 text-left">
            <dl className="grid grid-cols-3 gap-y-2 text-sm">
              <dt className="col-span-1 text-gray-500">Your email</dt>
              <dd className="col-span-2 text-gray-900">{myEmail || '-'}</dd>

              <dt className="col-span-1 text-gray-500">Role</dt>
              <dd className="col-span-2 text-gray-900 capitalize">{inviteData.role || 'member'}</dd>

              {inviteData.invited_by_email && (
                <>
                  <dt className="col-span-1 text-gray-500">Invited by</dt>
                  <dd className="col-span-2 text-gray-900">{inviteData.invited_by_email}</dd>
                </>
              )}

              {expiresLabel && (
                <>
                  <dt className="col-span-1 text-gray-500">Expires</dt>
                  <dd className="col-span-2 text-gray-900">{expiresLabel}</dd>
                </>
              )}
            </dl>
            {wrongEmail}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleAccept}
              disabled={!!mismatch || submitLoading !== null}
              className={[
                'w-full rounded-xl px-6 py-3 font-semibold text-white shadow-md transition',
                mismatch || submitLoading ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              ].join(' ')}
            >
              {submitLoading === 'accept' ? (
                <span className="inline-flex items-center">
                  <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
                  </svg>
                  Joining…
                </span>
              ) : (
                'Accept Invitation'
              )}
            </button>

            <button
              onClick={handleDecline}
              disabled={submitLoading !== null}
              className="w-36 rounded-xl border border-gray-300 bg-white px-4 py-3 font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
            >
              {submitLoading === 'decline' ? 'Declining…' : 'Decline'}
            </button>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          Not you?{' '}
          <a href="/" className="text-blue-600 underline">
            Go back
          </a>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitePage;
